import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button, Col, Dropdown, Icon, Menu, Row, Statistic, Table } from 'antd';

import TableActions from '../../atoms/TableActions';
import AppTemplate from '../../templates/AppTemplate';
import Message from '../../atoms/Message';
import './Campaign.css';
import Taggable from '../../../containers/campaign/lineitem/Taggable';
import IODetails from '../../../containers/campaign/IODetails';

const StyledTable = styled(Table)`
  tbody {
    tr td:nth-child(2) {
      padding: 0;
    }
    td a {
      display: block;
      padding: 16px 16px;
    }
  }
`;

const Campaign = ({
  archived,
  toggleArchived,
  pagination,
  campaign,
  loading,
  rejectedCampaign,
  tableData,
  columns,
  handleAction,
  handlePauseCampaigns,
  handleRunCampaigns,
  handleArchiveCampaigns,
  handleTableChange,
  handleCreateLineItem,
  selectedRowKeys,
  onSelectedRowKeysChange,
  disableTableActions,
  disableCopyLineItems,
  disablePasteLineItems,
  creatingNewLineItem,
}) => {
  return (
    <AppTemplate>
      {rejectedCampaign && <Message type="error">Could not load the campaign.</Message>}

      {campaign && (
        <div>
          <Row type="flex" justify="space-between" align="middle">
            <Col>
              <h1>
                {campaign.name}
                <Button
                  type="primary"
                  size="large"
                  onClick={handleCreateLineItem}
                  loading={creatingNewLineItem}
                >
                  + New Line Item
                </Button>
                <IODetails campaign={campaign} />
              </h1>
            </Col>
            <Col>
              <Statistic title="Start Date" value={campaign.start_date || 'N/A'} />
            </Col>
            <Col>
              <Statistic title="End Date" value={campaign.end_date || 'N/A'} />
            </Col>
            <Col>
              <Statistic title="IO Value" prefix="$" value={campaign.io_value || 'N/A'} />
            </Col>
          </Row>

          <Row type="flex" justify="space-between" align="middle">
            <Col>
              <TableActions
                handlePauseCampaigns={handlePauseCampaigns}
                handleRunCampaigns={handleRunCampaigns}
                handleArchiveCampaigns={handleArchiveCampaigns}
                disabled={disableTableActions}
              >
                <Dropdown
                  overlay={
                    <Menu onClick={({ key }) => handleAction(key)}>
                      <Menu.Item key="copy-line" disabled={disableCopyLineItems}>
                        Copy line items
                      </Menu.Item>
                      <Menu.Item key="paste-line" disabled={disablePasteLineItems}>
                        Paste line items
                      </Menu.Item>
                      <Menu.Item key="review" disabled={disableCopyLineItems}>
                        Place line items in review
                      </Menu.Item>
                      <Menu.Item key="approve" disabled={disableCopyLineItems}>
                        Approve line items
                      </Menu.Item>
                    </Menu>
                  }
                >
                  <Button>
                    More actions <Icon type="down" />
                  </Button>
                </Dropdown>
              </TableActions>
            </Col>
            <Col>
              <Taggable />
            </Col>
          </Row>

          <div className="tableContainer">
            <StyledTable
              rowKey="id"
              pagination={pagination}
              onChange={handleTableChange}
              loading={loading}
              rowSelection={{
                selectedRowKeys,
                onChange: onSelectedRowKeysChange,
              }}
              dataSource={tableData}
              columns={columns}
              locale={{ emptyText: 'No line items found for this campaign.' }}
            />
            <Button onClick={toggleArchived} loading={loading}>
              {archived && 'Back to Unarchived'}
              {!archived && 'View Archived'}
            </Button>
          </div>
        </div>
      )}
    </AppTemplate>
  );
};

Campaign.defaultProps = {
  archived: false,
  rejectedCampaign: false,
  selectedRowKeys: [],
};

const tableData = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    type: PropTypes.string,
    status: PropTypes.boolean,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    impressions: PropTypes.number,
  })
);

Campaign.propTypes = {
  archived: PropTypes.bool,
  campaign: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  columns: PropTypes.any.isRequired,
  creatingNewLineItem: PropTypes.bool.isRequired,
  disableCopyLineItems: PropTypes.bool.isRequired,
  disablePasteLineItems: PropTypes.bool.isRequired,
  disableTableActions: PropTypes.bool.isRequired,
  handleAction: PropTypes.func.isRequired,
  handleArchiveCampaigns: PropTypes.func.isRequired,
  handleCreateLineItem: PropTypes.func.isRequired,
  handlePauseCampaigns: PropTypes.func.isRequired,
  handleRunCampaigns: PropTypes.func.isRequired,
  handleTableChange: PropTypes.func.isRequired,
  history: PropTypes.any,
  loading: PropTypes.bool.isRequired,
  onSelectedRowKeysChange: PropTypes.func.isRequired,
  pagination: PropTypes.any,
  rejectedCampaign: PropTypes.bool,
  selectedRowKeys: PropTypes.arrayOf(PropTypes.number),
  tableData,
  toggleArchived: PropTypes.func,
};

export default Campaign;
